import React, {useContext, useState} from 'react';
import {useMutation} from '@apollo/client';
import {DocumentNode} from 'graphql';
import {Form, Formik, FormikHelpers, FormikValues} from 'formik';
import {LanguageId} from '../../../models/utils/Languages';
import FieldConfiguration from '../../../models/form/FieldConfiguration';
import ExternalButtonSelector from '../components/ExternalButtonSelector';
import SimpleQuestionTranslations from '../components/SimpleQuestionTranslations';
import withTranslations, {InjectedTranslationsProps} from './withTranslations';
import {SectionId} from '../../../models/form/types';
import {FieldConfigurationQL} from '../../../models/graphql/FieldConfigurationQL';
import SimpleFormCompanion from './SimpleFormCompanion';
import FieldLabel from './FieldLabel';
import ControlBlock from './ControlBlock';
import {ActionType, FormTypeContext} from '../types';

export interface SimpleFieldFormProps {
  sectionId: SectionId | undefined
  defaultLanguage: LanguageId
  configuration?: FieldConfiguration
  onCreateUpdate: (action: ActionType, el?: FieldConfiguration) => void;
}

interface SimpleFormProps extends InjectedTranslationsProps, SimpleFieldFormProps {
  createMutation: DocumentNode
  updateMutation: DocumentNode
  tip?: React.ReactNode
}

function SimpleFieldForm({
  sectionId,
  defaultLanguage,
  configuration,
  onCreateUpdate,
  translations,
  onAddTranslation,
  onDeleteTranslation,
  createMutation,
  updateMutation,
  tip,
}: SimpleFormProps) {
  const [createSubmit] = useMutation<{ field: FieldConfigurationQL }>(createMutation);
  const [updateSubmit] = useMutation<{ field: FieldConfigurationQL }>(updateMutation);
  const [loading, setLoading] = useState(false);
  const formTypeContext = useContext(FormTypeContext);

  const companion = new SimpleFormCompanion(sectionId, translations, formTypeContext, configuration);

  const onFormSubmit = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    setLoading(true);
    const input = companion.prepareInput(values);
    const submit = configuration ? updateSubmit({variables: input}) : createSubmit({variables: input});
    setTimeout(() => {
      companion.handleFieldSubmitRequest(submit, onCreateUpdate).finally(() => setLoading(false));
      helpersReact.setSubmitting(false);
    }, 400);
  };

  return (
    <>
      {tip}
      <Formik
        initialValues={companion.getInitialFormValues()}
        validationSchema={companion.getValidationObject()}
        onSubmit={onFormSubmit}>
        {({errors}) => (
          <Form>
            <FieldLabel/>
            <div className="pt-3">
              <SimpleQuestionTranslations languages={translations} defaultLanguage={defaultLanguage}
                deleteTranslation={onDeleteTranslation} label="Question" errors={errors}/>
              <ExternalButtonSelector className={'pt-3'} options={companion.labelManager.availableLanguages()}
                addItem={onAddTranslation}
                btnTitle={<>Add&nbsp;translation</>}/>
            </div>
            <div className="pt-5">
              <ControlBlock loading={loading} disabled={sectionId === undefined}/>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default withTranslations(SimpleFieldForm);
